import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/tracker";

const INTERESTS = ["GPU & AI Compute", "Computer Components", "Memory & Storage", "Servers & Workstations", "Networking", "Consumer Technology", "Mobile & Accessories", "Gaming / Consoles", "All Offers"];

const inputCls =
  "w-full bg-white border border-midnight/15 px-4 py-3 text-sm text-midnight placeholder:text-midnight/35 outline-none focus:border-gold transition-colors";

export const SubscribeBlock = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", country: "" });
  const [interests, setInterests] = useState([]);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (i) => setInterests((arr) => (arr.includes(i) ? arr.filter((x) => x !== i) : [...arr, i]));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please confirm you would like to receive marketing communications.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/subscribe", { ...form, interests, consent, source: "footer" });
      trackEvent("marketing_subscribed", { interests });
      setDone(true);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Subscription failed. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="border-y border-midnight/10 bg-white" data-testid="subscribe-block">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-2 gap-10">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-4">Market Offers</p>
          <h3 className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-midnight">
            Receive Royal Deals market offers and availability updates.
          </h3>
          <p className="text-midnight/55 text-sm mt-4 max-w-md leading-relaxed">
            Subscribe to receive selected product availability, technology offers and commercial updates from Royal Deals FZ-LLC.
          </p>
        </div>

        {done ? (
          <div className="flex items-center" data-testid="subscribe-confirmation">
            <p className="text-midnight/70 leading-relaxed border-l-2 border-gold pl-5">
              Subscription confirmed. You will receive selected Royal Deals FZ-LLC offers and availability updates at <span className="font-medium text-midnight">{form.email}</span>. Every email includes a working unsubscribe link.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="subscribe-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="Name *" value={form.name} onChange={set("name")} data-testid="subscribe-name" className={inputCls} />
              <input required type="email" placeholder="Business email *" value={form.email} onChange={set("email")} data-testid="subscribe-email" className={inputCls} />
              <input placeholder="Company (optional)" value={form.company} onChange={set("company")} data-testid="subscribe-company" className={inputCls} />
              <input required placeholder="Country *" value={form.country} onChange={set("country")} data-testid="subscribe-country" className={inputCls} />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="subscribe-interests">
              {INTERESTS.map((i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => toggle(i)}
                  className={`text-[11px] font-medium px-3 py-1.5 transition-colors ${interests.includes(i) ? "bg-midnight text-white" : "bg-white text-midnight/60 hairline hover:border-gold"}`}
                >
                  {i}
                </button>
              ))}
            </div>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="subscribe-consent">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#C5A059]" />
              <span className="text-xs text-midnight/60 leading-relaxed">
                I would like to receive commercial offers, product availability updates and marketing communications from Royal Deals FZ-LLC. I understand I can unsubscribe at any time.
              </span>
            </label>
            <button type="submit" disabled={busy} data-testid="subscribe-submit" className="bg-midnight text-white text-sm font-medium px-8 py-3.5 hover:bg-midnight-light transition-colors disabled:opacity-50">
              {busy ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
